import React from "react";
import { Button, Col, Divider, Flex, Form, Grid, Input, notification, Popconfirm, Row, Select, Skeleton } from "antd"

import { PropriedadesDoPopConfirm } from "../../../../Defaults";
import { SessaoContext } from "../../../../Contexts/SessaoContext";
import { Permissoes } from "../../../../Constants/Permissoes";
import { Restrito } from "../../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../../Components/Conjunto";
import { Ordenar } from "../../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../../Hooks/useGetParameterOnRoutes";
import { TypeTipoDeViagem } from "../../../../Models/Domain/Viagem/TipoDeViagem";
import { useContext, useEffect, useState } from "react";
import { TipoDeViagemService } from "../../../../Services/Domain/Viagem/TipoDeViagemService";
import { useForm } from "antd/lib/form/Form";

import { useMutation, useQuery } from "@tanstack/react-query";

import * as Tutoriais from "./tipoDeViagem.tutorial"
import AdicionarAjuda, { ITutorial } from "../../../../Components/AdicionarAjuda";
import { PropriedadesDeNotificacao } from "../../../../Utils/Notificacoes";

const ListagemDeTiposDeViagem: React.FC<{
    aoEditar?: (tipoDeViagem: TypeTipoDeViagem) => any,
    aoAdicionar?: () => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (tipoDeViagem: TypeTipoDeViagem) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const tipoDeViagemService = new TipoDeViagemService();

    interface IFiltro {
        ativo?: boolean,
        nome?: string
    }

    const [filtro, setFiltro] = useState<IFiltro>({});

    const tiposDeViagemDisponiveis = useQuery({
        queryKey: ["listarTiposDeViagem", filtro],
        gcTime: 0,
        queryFn: () => tipoDeViagemService.listarTiposDeViagem({
            ativo: filtro.ativo,
            nome: filtro.nome
        }),
        enabled: true,
    });

    const onFinish = (valores: IFiltro) => {
        setFiltro({
            ...valores
        });
    }

    const [form] = useForm<IFiltro>();

    const adicionar = permissoes?.includes(Permissoes.CriarTipoDeViagem);

    return (
        <React.Fragment>
            <Form<IFiltro> form={form} onFinish={onFinish} layout="vertical" autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro}>
                    <Col xs={24} xl={8} id={Tutoriais.IDs.filtroDeDescricao}>
                        <Form.Item<IFiltro> name="nome" label="Nome">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={4} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={adicionar ? 12 : 24} xl={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                        <Form.Item style={{ width: "100%" }}>
                            <Button
                                type="primary"
                                id={Tutoriais.IDs.botaoListar}
                                htmlType="submit"
                                disabled={tiposDeViagemDisponiveis.isFetching}
                                style={{ width: "100%" }}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} xl={3} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item style={{ width: "100%" }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeTipoDeViagem>
                        bordered
                        rowKey={(k) => k.codigo!}
                        locale={{ emptyText: "Nenhum tipo de viagem encontrado." }}
                        loading={tiposDeViagemDisponiveis.isFetching}
                        dataSource={tiposDeViagemDisponiveis.data?.data}
                        addTitleOn="lg"
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 3 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 24, sm: 12, lg: 3 },
                                key: "ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },

                            {
                                title: "Nome",
                                dataIndex: ["nome"],
                                breakpoints: { xs: 24, lg: 13 },
                                key: "nome",
                                sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                            },

                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                hidden: [Permissoes.AtualizarTipoDeViagem, Permissoes.DesativarTipoDeViagem, Permissoes.AtivarTipoDeViagem].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={(() => {
                                                if (item.ativo) {
                                                    return [
                                                        permissoes?.includes(Permissoes.AtualizarTipoDeViagem) && (
                                                            <Link
                                                                to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type="primary"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>
                                                        ),
                                                        permissoes?.includes(Permissoes.DesativarTipoDeViagem) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;

                                                                    tipoDeViagemService
                                                                        .desativarTipoDeViagem(item.codigo)
                                                                        .then(() => {
                                                                            tiposDeViagemDisponiveis.refetch();
                                                                        })
                                                                        .catch(() => {});
                                                                }}
                                                            >
                                                                <Button type="default" danger size="small" id={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                } else {
                                                    return [
                                                        permissoes?.includes(Permissoes.AtivarTipoDeViagem) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;

                                                                    tipoDeViagemService
                                                                        .ativarTipoDeViagem(item.codigo)
                                                                        .then(() => {
                                                                            tiposDeViagemDisponiveis.refetch();
                                                                        })
                                                                        .catch(() => {});
                                                                }}
                                                            >
                                                                <Button type="dashed" size="small">
                                                                    Reativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                }
                                            })()}
                                        />
                                    );
                                },
                            },
                        ]}
                        
                        csv={{
                            enabled: true,
                            filename: "tipos-de-viagem.csv",
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda tutoriais={[].filter((v) => v != false) as ITutorial[]} />
        </React.Fragment>
    );

}

const CadastroDeTipoDeViagem: React.FC<{
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any,
    tipoDeViagemEmAlteracao?: TypeTipoDeViagem
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const tipoDeViagemService = new TipoDeViagemService();

    const { tipoDeViagemEmAlteracao } = props;

    useEffect(() => {
        if (tipoDeViagemEmAlteracao != undefined) {
            form.setFieldsValue(tipoDeViagemEmAlteracao)
        }
    }, [tipoDeViagemEmAlteracao])

    const [form] = useForm<TypeTipoDeViagem>();

    const inserirTipoDeViagem = useMutation({
        mutationFn: (payload: TypeTipoDeViagem) => tipoDeViagemService.inserirTipoDeViagem(payload),
        onSuccess() {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })

            props.depoisDeFinalizar?.();
        },
    })

    const atualizarTipoDeViagem = useMutation({
        mutationFn: (payload: TypeTipoDeViagem) => tipoDeViagemService.atualizarTipoDeViagem(payload),
        onSuccess() {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro atualizado com sucesso.')
            })

            props.depoisDeFinalizar?.();
        },
    })

    const onFinish = (valores: TypeTipoDeViagem) => {
        const estaEditando = tipoDeViagemEmAlteracao != undefined;
        if (estaEditando) {
            atualizarTipoDeViagem.mutate({ ...tipoDeViagemEmAlteracao, ...valores, });
        } else {
            inserirTipoDeViagem.mutate(valores);
        }
    }

    return <React.Fragment>
        <Form<TypeTipoDeViagem> form={form} onFinish={onFinish} layout="vertical" autoComplete="off"
            validateTrigger={"onBlur"}
        >
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Form.Item<TypeTipoDeViagem>
                        name={"nome"}
                        label={"Nome"}
                        rules={[{ required: true, message: "É necessário específicar o nome!" }]}
                    >
                        <Input placeholder="Nome" />
                    </Form.Item>
                </Col>

                <Col span={24}>
                    <Flex align="center" justify="center" gap={16}>
                        {props.aoCancelar &&
                            <Button danger type="primary" key={"cancel"} onClick={() => { props.aoCancelar && props.aoCancelar() }}>
                                Cancelar
                            </Button>
                        }
                        <Button type="primary" key={"submit"} htmlType="submit">
                            Salvar
                        </Button>
                    </Flex>
                </Col>

            </Row>
        </Form>

        <AdicionarAjuda tutoriais={[]} />

    </React.Fragment>
}

export const TipoDeViagem: React.FC<{}> = (props) => {
    const [tipoDeViagemEmAlteracao, setTipoDeViagemEmAlteracao] = useState<TypeTipoDeViagem | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterTipoDeViagem = useMutation({
        mutationFn: (id: number) => new TipoDeViagemService().listarTiposDeViagem({ codigo: id, ativo: true }),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setTipoDeViagemEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarTiposDeViagem, Permissoes.CriarTipoDeViagem, Permissoes.AtualizarTipoDeViagem, Permissoes.AtivarTipoDeViagem, Permissoes.DesativarTipoDeViagem])
    useWatchParameterOnRoutes({
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))],
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterTipoDeViagem.mutate(Number(param)); },
    })

    return <React.Fragment>
        <>
            <h2 style={{ marginTop: 0 }}>
                {
                    rotaDeVisualizacao ? "Detalhes do Tipo de Viagem" : rotaDeEdicao ? "Edição do Tipo de Viagem" : "Cadastro de Tipo de Viagem"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarTiposDeViagem) &&
                    <Route path="*" index element={<ListagemDeTiposDeViagem
                        hrefDoBotaoAdicionar={raiz + "/cadastro"}
                        hrefDoBotaoEditar={(item) => {
                            if (item.codigo) {
                                return raiz + `/editar?id=${item.codigo}`
                            }
                        }}

                        aoEditar={(item) => {
                            setTipoDeViagemEmAlteracao(item);
                        }}
                    />} />
                }

                {
                    permissoes?.includes(Permissoes.CriarTipoDeViagem) &&
                    <Route path="/cadastro" element={<CadastroDeTipoDeViagem
                        aoCancelar={() => { navigate(raiz) }}
                        depoisDeFinalizar={() => { navigate(raiz) }}
                    />} />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarTipoDeViagem) &&
                    <Route path="/editar" element={
                        tipoDeViagemEmAlteracao == undefined ?
                            <Skeleton active />
                            :
                            <CadastroDeTipoDeViagem
                                tipoDeViagemEmAlteracao={tipoDeViagemEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />} />
                }
            </Routes>


        </>
    </React.Fragment>
}